<template>
  <div class="no-access-container">
    <div class="no-access-card">
      <div class="icon-container">
        <svg class="no-access-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      
      <h1 class="no-access-title">Access Denied</h1>
      
      <div class="no-access-message">
        <p class="main-message">
          You don't have permission to access this page.
        </p>
        <p class="sub-message">
          Contact your administrator to request access to this feature.
        </p>
      </div>

      <div class="page-info" v-if="requestedPage">
        <div class="info-item">
          <span class="info-label">Requested Page:</span>
          <span class="info-value">{{ formatPageName(requestedPage) }}</span>
        </div>
      </div>

      <div class="actions">
        <button @click="goHome" class="home-button">
          <svg class="home-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Go to Dashboard
        </button>
        
        <button @click="goBack" class="back-button">
          <svg class="back-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Go Back
        </button>
      </div>

      <div class="contact-info">
        <p class="contact-text">
          Need access? Contact us at 
          <a href="mailto:support@routebox.com" class="contact-link">support@routebox.com</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import AuthService from '../services/AuthService'

export default {
  name: 'NoAccessPage',
  setup() {
    const router = useRouter()
    const route = useRoute()
    
    const requestedPage = ref('')

    const formatPageName = (pageName) => {
      return pageName.charAt(0).toUpperCase() + pageName.slice(1)
    }

    const goHome = async () => {
      // Check if user has access to home page
      const user = AuthService.getCurrentUser()
      if (user) {
        const permissions = await AuthService.getUserPermissions(user.uid)
        if (permissions.home) {
          router.push('/home')
        } else {
          // Find the first page they have access to
          const availablePages = Object.keys(permissions).filter(page => permissions[page])
          if (availablePages.length > 0) {
            router.push(`/${availablePages[0]}`)
          } else {
            router.push('/pending')
          }
        }
      } else {
        router.push('/login')
      }
    }

    const goBack = () => {
      if (window.history.length > 1) {
        router.go(-1)
      } else {
        goHome()
      }
    }

    onMounted(() => {
      // Get the requested page from query params or route
      requestedPage.value = route.query.page || route.path.replace('/', '') || 'this page'
    })

    return {
      requestedPage,
      formatPageName,
      goHome,
      goBack
    }
  }
}
</script>

<style scoped>
.no-access-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
}

.no-access-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 50px 40px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.icon-container {
  margin-bottom: 30px;
}

.no-access-icon {
  width: 80px;
  height: 80px;
  color: #e53e3e;
}

.no-access-title {
  font-size: 28px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 20px 0;
  font-family: 'Sansation', sans-serif;
}

.no-access-message {
  margin-bottom: 30px;
}

.main-message {
  font-size: 18px;
  color: #4a5568;
  margin: 0 0 10px 0;
  font-weight: 500;
}

.sub-message {
  font-size: 16px;
  color: #718096;
  margin: 0;
  line-height: 1.5;
}

.page-info {
  background: #f7fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label {
  font-weight: 600;
  color: #4a5568;
  font-size: 14px;
}

.info-value {
  color: #2d3748;
  font-size: 14px;
  font-weight: 500;
}

.actions {
  display: flex;
  gap: 12px;
  margin-bottom: 30px;
}

.home-button, .back-button {
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.home-button {
  background: #0077c2;
  color: white;
  border: none;
}

.home-button:hover {
  background: #005fa3;
  transform: translateY(-1px);
}

.back-button {
  background: #f7fafc;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}

.back-button:hover {
  background: #edf2f7;
  border-color: #cbd5e0;
}

.home-icon, .back-icon {
  width: 16px;
  height: 16px;
}

.contact-info {
  border-top: 1px solid #e2e8f0;
  padding-top: 20px;
}

.contact-text {
  color: #718096;
  font-size: 14px;
  margin: 0;
}

.contact-link {
  color: #0077c2;
  text-decoration: none;
  font-weight: 600;
}

.contact-link:hover {
  text-decoration: underline;
}

/* Responsive design */
@media (max-width: 600px) {
  .no-access-card {
    padding: 30px 20px;
    margin: 10px;
  }
  
  .no-access-title {
    font-size: 24px;
  }
  
  .actions {
    flex-direction: column;
  }
}
</style>
